/**
 * Native iOS UITabBar Integration
 *
 * This hook provides seamless native UITabBar integration for iPhone devices.
 * Uses the NativeTabBarPlugin Capacitor plugin for true iOS glass effect.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useIsIOS } from '@/hooks/useIsIOS';
import { useLogger } from '@/hooks/utils/use-logger';
import NativeTabBar, { type TabItem } from '@/lib/native-tab-bar';

export type { TabItem };

export interface NavItem {
  id: string;
  label: string;
  icon: string;
  path: string;
}

/**
 * Hook to manage native iOS UITabBar integration
 * Provides seamless native navigation on iOS devices with fallback to web
 */
export function useNativeIOSDock(
  navItems: NavItem[],
  onPreload?: (path: string) => void,
  onTabSelect?: (item: NavItem) => boolean | void,
  activeIndexOverride?: number | null,
  enabled: boolean = true,
) {
  const navigate = useNavigate();
  const location = useLocation();
  const isIOS = useIsIOS();
  const [isNativeActive, setIsNativeActive] = useState(false);
  const [isNativeAvailable, setIsNativeAvailable] = useState(false);
  const logger = useLogger('useNativeIOSDock');
  const listenerRef = useRef<{ remove: () => void } | null>(null);
  const initializedRef = useRef(false);

  // Check availability on mount
  useEffect(() => {
    if (!isIOS || typeof window === 'undefined') {
      setIsNativeAvailable(false);
      return;
    }

    // Check for iPad - we don't want native tab bar on iPad
    if (/ipad/i.test(navigator.userAgent)) {
      setIsNativeAvailable(false);
      return;
    }

    NativeTabBar.isAvailable()
      .then(({ available }) => {
        setIsNativeAvailable(available);
      })
      .catch(() => {
        setIsNativeAvailable(false);
      });
  }, [isIOS]);

  // Handle native tab selection
  const handleNativeTabSelect = useCallback(
    (index: number, path: string) => {
      const item = navItems[index];
      if (!item) return;

      const handled = onTabSelect?.(item);
      if (handled) return;

      const targetPath = item.path || path;
      if (targetPath && targetPath !== location.pathname) {
        // Preload if available
        if (onPreload) {
          onPreload(targetPath);
        }

        // Navigate with fromDock state
        navigate(targetPath, { state: { fromDock: true } });
      }
    },
    [navItems, onTabSelect, location.pathname, navigate, onPreload],
  );

  // Show/hide native dock based on enabled flag (e.g. hide on onboarding/auth)
  useEffect(() => {
    if (!initializedRef.current) return;
    if (enabled) {
      NativeTabBar.show({
        items: navItems.map((item) => ({
          id: item.id,
          label: item.label,
          icon: item.icon,
          path: item.path,
        })),
      }).catch(() => {});
    } else {
      NativeTabBar.hide().catch(() => {});
    }
  }, [enabled, navItems]);

  // Initialize native dock
  useEffect(() => {
    if (!isNativeAvailable || initializedRef.current || !enabled) {
      return;
    }

    const initNativeTabBar = async () => {
      try {
        // Convert NavItems to TabItems
        const tabItems: TabItem[] = navItems.map((item) => ({
          id: item.id,
          label: item.label,
          icon: item.icon,
          path: item.path,
        }));

        // Show native tab bar
        await NativeTabBar.show({ items: tabItems });

        // Add listener for tab selection
        listenerRef.current = await NativeTabBar.addListener('tabSelected', (data) => {
          handleNativeTabSelect(data.index, data.path);
        });

        initializedRef.current = true;
        setIsNativeActive(true);
        logger.info('Native iOS tab bar initialized');
      } catch (error) {
        logger.warn('Failed to initialize native tab bar', { error });
        setIsNativeActive(false);
      }
    };

    initNativeTabBar();

    // Cleanup
    return () => {
      if (listenerRef.current) {
        listenerRef.current.remove();
        listenerRef.current = null;
      }
      NativeTabBar.hide().catch(() => {
        // Silently fail cleanup
      });
      initializedRef.current = false;
    };
  }, [enabled, isNativeAvailable, navItems, handleNativeTabSelect, logger]);

  // Sync active tab with current route
  useEffect(() => {
    if (!isNativeActive) return;

    const resolvedIndex =
      typeof activeIndexOverride === 'number'
        ? activeIndexOverride
        : navItems.findIndex((item) => location.pathname === item.path);

    if (resolvedIndex >= 0) {
      NativeTabBar.selectTab({ index: resolvedIndex }).catch(() => {
        // Silently fail sync
      });
    }
  }, [location.pathname, navItems, isNativeActive, activeIndexOverride]);

  return isNativeActive;
}
